import React from "react";
import { Button, Checkbox, Form, Input, Tooltip } from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import Select from "@components/ResponsiveSelect";
import {
  createEmptyEquipmentCategoryForm,
  EQUIPMENT_CATEGORY_FORM_TYPE_OPTIONS,
  parseEquipmentCategoryFormOptions,
} from "@/utils/equipment-category-forms";
import "./index.css";

const EquipmentCategoryFormsEditor = ({ form, readOnly = false }) => {
  const validateLabel = (rowIndex) => (_, value) => {
    if (typeof value !== "string" || !value.trim()) {
      return Promise.reject(new Error("Label is required"));
    }
    const rows = form.getFieldValue("forms") || [];
    const duplicateIndex = rows.findIndex(
      (row, index) => index !== rowIndex && row?.label === value
    );
    return duplicateIndex >= 0
      ? Promise.reject(new Error("Label must be unique"))
      : Promise.resolve();
  };

  const validateExtra = (rowIndex) => (_, value) => {
    const formType = form.getFieldValue(["forms", rowIndex, "formType"]);
    if (formType === "select" && !parseEquipmentCategoryFormOptions(value).length) {
      return Promise.reject(new Error("Add at least one Select option"));
    }
    return Promise.resolve();
  };

  return (
    <Form.List name="forms">
      {(fields, { add, remove, move }) => (
        <div className="equipment-category-forms-editor">
          {fields.length === 0 && (
            <div className="equipment-category-forms-empty">No dynamic fields</div>
          )}
          {fields.map((field, index) => (
            <div className="equipment-category-form-row" key={field.key}>
              <Form.Item
                {...field}
                label="Label"
                name={[field.name, "label"]}
                fieldKey={[field.fieldKey, "label"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ validator: validateLabel(index) }]}
              >
                <Input disabled={readOnly} allowClear />
              </Form.Item>
              <Form.Item
                {...field}
                label="Description"
                name={[field.name, "description"]}
                fieldKey={[field.fieldKey, "description"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Input disabled={readOnly} allowClear />
              </Form.Item>
              <Form.Item
                {...field}
                label="Unit"
                name={[field.name, "unit"]}
                fieldKey={[field.fieldKey, "unit"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <Input disabled={readOnly} allowClear />
              </Form.Item>
              <Form.Item
                {...field}
                label="Form Type"
                name={[field.name, "formType"]}
                fieldKey={[field.fieldKey, "formType"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                rules={[{ required: true, message: "Form Type is required" }]}
              >
                <Select disabled={readOnly} options={EQUIPMENT_CATEGORY_FORM_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(previous, current) => (
                  previous?.forms?.[index]?.formType !== current?.forms?.[index]?.formType
                )}
              >
                {() => {
                  const isSelect = form.getFieldValue(["forms", index, "formType"]) === "select";
                  return (
                    <Form.Item
                      {...field}
                      label={isSelect ? "Extra / Options" : "Extra"}
                      name={[field.name, "extra"]}
                      fieldKey={[field.fieldKey, "extra"]}
                      labelCol={{ span: 24 }}
                      wrapperCol={{ span: 24 }}
                      rules={[{ validator: validateExtra(index) }]}
                    >
                      <Input
                        disabled={readOnly}
                        allowClear
                        placeholder={isSelect ? "Option A; Option B, Option C" : undefined}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
              <Form.Item
                {...field}
                className="equipment-category-form-required"
                label="Required"
                name={[field.name, "require"]}
                fieldKey={[field.fieldKey, "require"]}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                valuePropName="checked"
              >
                <Checkbox disabled={readOnly} />
              </Form.Item>
              {!readOnly && (
                <div className="equipment-category-form-actions">
                  <Tooltip title="Move up">
                    <Button
                      aria-label="Move field up"
                      icon={<ArrowUpOutlined />}
                      disabled={index === 0}
                      onClick={() => move(index, index - 1)}
                    />
                  </Tooltip>
                  <Tooltip title="Move down">
                    <Button
                      aria-label="Move field down"
                      icon={<ArrowDownOutlined />}
                      disabled={index === fields.length - 1}
                      onClick={() => move(index, index + 1)}
                    />
                  </Tooltip>
                  <Tooltip title="Delete field">
                    <Button
                      aria-label="Delete field"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button
              className="equipment-category-form-add"
              icon={<PlusOutlined />}
              onClick={() => add(createEmptyEquipmentCategoryForm())}
            >
              Add Field
            </Button>
          )}
        </div>
      )}
    </Form.List>
  );
};

export default EquipmentCategoryFormsEditor;
